import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { AiActionItem, AiActionItemsBlock, AiActionItemsTaskProjection } from '../../models/ai-assistant.models';

const MAX_ROWS = 8;

@Component({
  selector: 'app-response-action-items',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './response-action-items.component.html',
  styleUrl: './response-action-items.component.css',
})
export class ResponseActionItemsComponent {
  @Input({ required: true }) block!: AiActionItemsBlock;

  private expandedIds = new Set<string>();

  get rows(): AiActionItem[] {
    return this.block.items.slice(0, MAX_ROWS);
  }

  get shownCount(): number {
    return this.rows.length;
  }

  get totalCount(): number {
    return this.block.counts.total;
  }

  get openCount(): number {
    return this.block.counts.open;
  }

  get verifiedCount(): number {
    return this.block.counts.verified;
  }

  get pendingCount(): number {
    return Math.max(0, this.block.counts.total - this.block.counts.open - this.block.counts.verified);
  }

  get hasHighRisk(): boolean {
    return this.block.flags.hasHighRiskActionItem;
  }

  get hasEvidence(): boolean {
    return this.block.flags.hasEvidenceRequired;
  }

  get hasAssetLinks(): boolean {
    return this.block.flags.hasDirectAssetLinks;
  }

  toggleExpand(id: string, event: Event): void {
    event.stopPropagation();
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedIds.has(id);
  }

  rowKey(item: AiActionItem): string {
    return item.actionItemId || item.text || String(Math.random());
  }

  get task(): AiActionItemsTaskProjection | undefined {
    return this.block.task;
  }

  badgeClass(value: string | undefined): string {
    return `ai-pill tone-${(value ?? '').toLowerCase().replace(/\s+/g, '-')}`;
  }

  getAssigneeName(item: AiActionItem): string | undefined {
    if (!item.assigned) return undefined;
    if (typeof item.assigned === 'string') return item.assigned || undefined;
    return item.assigned.name || item.assigned.email || undefined;
  }

  getAssigneeAvatar(item: AiActionItem): string | undefined {
    if (!item.assigned || typeof item.assigned === 'string') return undefined;
    return item.assigned.avatarUrl || item.assigned.photo || item.assigned.avatar || item.assigned.profilePhoto || undefined;
  }

  getInitials(name: string): string {
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
  }
}
